import React, { Suspense, useCallback, useEffect, useState } from "react";
import { useHistory, useParams } from "react-router-dom";
import { Option } from "react-select/src/filters";

import { Container, Link, Row } from "../components";
import { PulseLoader } from "../components/pulse_loader";
import { Amenity, Pricing } from "../data";
import { useGetPartnerOffering } from "../data/api";
import { buildPathWithParams, UrlParams, useQueryString } from "../hooks";
import { formatCurrency } from "../lib/currency/currency";
import { useI18n } from "../lib/i18n/i18n_provider";
import { uniqueBy } from "../lib/js_utils/array_utils";
import { isEmpty } from "../lib/js_utils/lang_utils";
import {
  SpaceOffering,
  useLocationV2Query,
  useSpaceOfferingsQuery,
  useSpaceV2Query,
} from "./api";
import { Grid } from "./grid";
import { Spacer } from "./spacer";
import { Text } from "./text";
import { FAQ } from "./template_offering_details_base";
import {
  CityPicker,
  CityPickerMobile,
  EmptyState,
  ItemCardHeader,
  TemplateOfferingListingLayout,
} from "./template_offering_listing_base";
import { PartnerConfig } from "./partner_config";
import { PartnerOfferingLocation } from "../data/partner_offering";
import { useIsWework } from "./hooks/use_is_wework";

interface TemplateOfferingListingDayPassProps {
  showTalkWithUsPrompt?: boolean;
  showPrice?: boolean;
  calculateLowestPrice?: boolean;
  title?: string;
  partnerConfig: PartnerConfig;
  data: {
    faqs?: FAQ[];
    description?: React.ReactNode;
  };
}

interface TemplateOfferingListingDayPassParams {
  offeringID: string;
}

export function TemplateOfferingListingDayPass(
  props: TemplateOfferingListingDayPassProps
) {
  const {
    showTalkWithUsPrompt,
    title,
    partnerConfig,
    data,
    showPrice,
    calculateLowestPrice,
  } = props;
  const { favicon, hubspotForm, showLanguageSelector = false } = partnerConfig;
  const { faqs, description } = data;
  const pageParams = useParams<TemplateOfferingListingDayPassParams>();
  const history = useHistory();
  const searchParams = useQueryString<DayPassFilterSearchParams>();
  const [filter, setFilter] = useState<DayPassFilter>(
    deserializeDayPassFilterSearchParams(searchParams)
  );
  const partnerOffering = useGetPartnerOffering(pageParams.offeringID);
  const locations = partnerOffering.offeringLocations;
  const handleCityChange = useCallback((city?: string) => {
    setFilter((prevFilter) => ({
      ...prevFilter,
      city,
      locationID: undefined,
    }));
  }, []);

  const handlePressResetFilter = useCallback(() => {
    setFilter({
      city: undefined,
    });
  }, []);

  useEffect(() => {
    const pathWithParams = buildPathWithParams(
      window.location.pathname,
      filter
    );
    history.replace(pathWithParams);
  }, [filter, history]);

  const cities = uniqueBy(
    locations.map((l) => ({
      value: l.location.city as string,
      label: l.location.city as string,
      data: l.location,
    })),
    (l) => l.value
  );

  return (
    <TemplateOfferingListingLayout
      title={title || partnerOffering.partner.name}
      description={description}
      favicon={favicon}
      showLanguageSelector={showLanguageSelector}
      partnerConfig={partnerConfig}
      showTalkWithUsPrompt={showTalkWithUsPrompt}
      hubspotForm={hubspotForm}
      offeringID={pageParams.offeringID}
      faqs={faqs}
      partnerOffering={partnerOffering}
      desktopFilters={
        <DesktopFilters
          onCityChange={handleCityChange}
          filter={filter}
          cities={cities}
        />
      }
      mobileFilters={
        <MobileFilters
          cities={cities}
          onFilterChange={setFilter}
          filter={filter}
        />
      }
      listingContent={
        <Container>
          <Suspense fallback={<PulseLoader />}>
            <DayPassGallery
              partnerOfferingID={pageParams.offeringID}
              filter={filter}
              onPressResetFilter={handlePressResetFilter}
              showPrice={showPrice}
              calculateLowestPrice={calculateLowestPrice}
            />
          </Suspense>
        </Container>
      }
    />
  );
}

interface DayPassGalleryProps {
  filter: DayPassFilter;
  partnerOfferingID: string;
  onPressResetFilter: () => void;
  showPrice?: boolean;
  calculateLowestPrice?: boolean;
}

function DayPassGallery(props: DayPassGalleryProps) {
  const {
    filter,
    partnerOfferingID,
    onPressResetFilter,
    showPrice,
    calculateLowestPrice,
  } = props;
  const partnerOffering = useGetPartnerOffering(partnerOfferingID, {
    city: filter.city,
  });

  if (isEmpty(partnerOffering.offeringLocations)) {
    return (
      <EmptyState
        hasFilter={!!filter.city}
        onPressResetFilter={onPressResetFilter}
      />
    );
  }

  const getOfferingDetailsPath = (l: PartnerOfferingLocation) => {
    const targetPath = filter.fromCatalog
      ? `/offerings/${partnerOfferingID}?spaceID=${l.spaceID}&${filter.fromCatalog}`
      : `/offerings/${partnerOfferingID}?spaceID=${l.spaceID}`;
    return buildPathWithParams(targetPath, filter);
  };

  return (
    <Container>
      <Grid itemsPerRow={{ md: 3, default: 1 }} spacing={16}>
        {partnerOffering.offeringLocations.map((l) => (
          <Container key={l.id} height="100%" paddingBottom={16}>
            <Link
              style={{
                height: "100%",
                display: "flex",
                flexDirection: "column",
              }}
              to={getOfferingDetailsPath(l)}
              onPress={() => {
                setTimeout(() => {
                  window.scrollTo({ top: 0 });
                }, 0);
              }}
            >
              <Container
                flex={1}
                borderWidth={1}
                borderRadius={5}
                borderColor="#D7D5DD"
              >
                <Suspense
                  fallback={
                    <Container minHeight={240} center>
                      <PulseLoader />
                    </Container>
                  }
                >
                  <DayPassGalleryItem
                    locationID={l.location.id}
                    spaceID={l.spaceID}
                    showPrice={showPrice}
                    featuredPromoDescription={l.featuredPromoDescription}
                    calculateLowestPrice={calculateLowestPrice}
                  />
                </Suspense>
              </Container>
            </Link>
          </Container>
        ))}
      </Grid>
    </Container>
  );
}

interface DayPassGalleryItemProps {
  locationID: string;
  spaceID: string;
  showPrice?: boolean;
  featuredPromoDescription?: string;
  calculateLowestPrice?: boolean;
}

function DayPassGalleryItem(props: DayPassGalleryItemProps) {
  const {
    locationID,
    spaceID,
    showPrice = false,
    featuredPromoDescription,
    calculateLowestPrice = false,
  } = props;
  const location = useLocationV2Query(locationID);
  const space = useSpaceV2Query(spaceID);
  const { data: spaceOfferings } = useSpaceOfferingsQuery(spaceID);
  let price = space.pricings[0];
  if (calculateLowestPrice && spaceOfferings && spaceOfferings.length > 0) {
    price.price = Math.min(
      price.price,
      ...spaceOfferings
        .filter((p: SpaceOffering) => p.price && p.numberOfEntries)
        .map((p: SpaceOffering) => {
          // @ts-ignore
          const avgPrice = p.price / p.numberOfEntries;
          return Math.round(avgPrice * 100) / 100;
        })
    );
  }

  return (
    <Container flex={1}>
      <ItemCardHeader
        coverImage={{ src: location.images[0].small.url, alt: location.name }}
        title={location.name}
        subTitle={`${location.streetAddress}, ${location.city}`}
        featuredPromoDescription={featuredPromoDescription}
      />
      <Spacer size={30} />
      {showPrice && (
        <Container padding={8}>
          <Row alignItems="center" justifyContent="flex-end">
            <DayPassPricing pricing={price} currency={space.currencyCode} />
          </Row>
        </Container>
      )}
    </Container>
  );
}

interface DayPassPricingProps {
  pricing: Pricing;
  currency: string | null;
}

export function DayPassPricing(props: DayPassPricingProps) {
  const { pricing, currency } = props;
  const { fbt, locale } = useI18n();

  const isWeWork = useIsWework();
  return (
    <Row alignItems="center">
      <Text>{fbt("Start from")}</Text>
      <Spacer size={4} direction="horizontal" />
      <Text weight="bold">
        {formatCurrency(pricing.price, currency, locale.languageTag)}
      </Text>
      <Spacer size={4} direction="horizontal" />
      <Text>{isWeWork ? ` ${fbt("before VAT per day")}` : fbt("/day")}</Text>
    </Row>
  );
}

interface AmenitiesProps {
  amenities: Amenity[];
}

export function Amenities(props: AmenitiesProps) {
  const { amenities } = props;

  return (
    <Row wrap>
      {amenities.map((amenity) => (
        <React.Fragment key={amenity.name}>
          <img
            style={{ width: 24, height: 24 }}
            src={amenity.iconURL}
            alt={amenity.name}
          />
          <Spacer size={8} direction="horizontal" />
        </React.Fragment>
      ))}
    </Row>
  );
}

interface DesktopFiltersProps {
  onCityChange: (city?: string) => void;
  filter: DayPassFilter;
  cities: Option[];
}

function DesktopFilters(props: DesktopFiltersProps) {
  const { filter, cities, onCityChange } = props;

  return (
    <Row>
      <Container maxWidth={308} flex={1}>
        <CityPicker
          onChange={onCityChange}
          cities={cities}
          value={filter.city}
        />
      </Container>
    </Row>
  );
}

interface MobileFiltersProps {
  onFilterChange: (filter: DayPassFilter) => void;
  filter: DayPassFilter;
  cities: Option[];
}

function MobileFilters(props: MobileFiltersProps) {
  const { cities, filter, onFilterChange } = props;
  const [localFilter, setLocalFilter] = useState(filter);

  const handleMainCityChange = useCallback(
    (city?: string) => {
      setLocalFilter((prevFilter) => ({
        ...prevFilter,
        city,
      }));
      onFilterChange({
        ...localFilter,
        city,
      });
    },
    [localFilter, onFilterChange]
  );

  return (
    <Container
      backgroundColor="white"
      boxShadow="0px 2px 7px 3px rgba(0, 0, 0, 0.25)"
      padding={16}
    >
      <Row>
        <Container flex={1}>
          <CityPickerMobile
            onChange={handleMainCityChange}
            cities={cities}
            value={filter.city}
          />
        </Container>
      </Row>
    </Container>
  );
}

interface DayPassFilterSearchParams {
  city?: string;
  locationID?: string;
  minCapacity?: string;
  maxCapacity?: string;
  /* Additional params support manage navigation from catalog page */
  fromCatalog?: string;
}

interface DayPassFilter extends UrlParams {
  city?: string;
  /* Additional params support manage navigation from catalog page */
  fromCatalog?: string;
}

function deserializeDayPassFilterSearchParams(
  searchParams?: DayPassFilterSearchParams
): DayPassFilter {
  return {
    city: searchParams?.city,
    fromCatalog: searchParams?.fromCatalog,
  };
}
